import { Moon, Monitor, Sun } from "lucide-react";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export type ThemePreference = "light" | "dark" | "system";
type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "shields:theme";
const QUERY = "(prefers-color-scheme: dark)";

type ThemeContextValue = {
  preference: ThemePreference;
  resolvedTheme: ResolvedTheme;
  setPreference: (value: ThemePreference) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readPreference(): ThemePreference {
  if (typeof window === "undefined") return "system";
  let stored: string | null = null;
  try { stored = window.localStorage.getItem(STORAGE_KEY); } catch { return "system"; }
  return stored === "light" || stored === "dark" || stored === "system" ? stored : "system";
}

function systemTheme(): ResolvedTheme {
  return typeof window !== "undefined" && window.matchMedia(QUERY).matches ? "dark" : "light";
}

function applyTheme(preference: ThemePreference): ResolvedTheme {
  const resolved = preference === "system" ? systemTheme() : preference;
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
  document.body.classList.toggle("dark", resolved === "dark");
  root.dataset['theme'] = resolved;
  root.dataset['themePreference'] = preference;
  root.style.colorScheme = resolved;
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", resolved === "dark" ? "#071a38" : "#fbfcff");
  return resolved;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>("system");
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>("light");

  useEffect(() => {
    const initial = readPreference();
    setPreferenceState(initial);
    setResolvedTheme(applyTheme(initial));
  }, []);

  useEffect(() => {
    const media = window.matchMedia(QUERY);
    const onChange = () => {
      if (preference === "system") setResolvedTheme(applyTheme("system"));
    };
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [preference]);

  function setPreference(value: ThemePreference) {
    try { window.localStorage.setItem(STORAGE_KEY, value); } catch { /* Continue without persistence when storage is blocked. */ }
    setPreferenceState(value);
    setResolvedTheme(applyTheme(value));
  }

  const value = useMemo(() => ({ preference, resolvedTheme, setPreference }), [preference, resolvedTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const value = useContext(ThemeContext);
  if (!value) throw new Error("useTheme must be used inside ThemeProvider");
  return value;
}

const options = [
  { value: "light" as const, label: "Light", icon: Sun },
  { value: "dark" as const, label: "Dark", icon: Moon },
  { value: "system" as const, label: "System", icon: Monitor },
];

export function ThemeControl({ compact = false }: { compact?: boolean }) {
  const { preference, resolvedTheme, setPreference } = useTheme();
  return <div>
    {!compact ? <div className="mb-3"><p className="font-bold text-foreground">Appearance</p><p className="text-sm text-muted-foreground">Choose Light, Dark, or follow your device automatically.</p></div> : null}
    <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Appearance">
      {options.map(({ value, label, icon: Icon }) => {
        const selected = preference === value;
        return <button key={value} type="button" role="radio" aria-checked={selected} onClick={() => setPreference(value)} className={`flex min-h-12 items-center justify-center gap-2 rounded-[var(--radius-control)] border px-3 text-sm font-bold transition ${selected ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-foreground hover:bg-muted"}`}><Icon className="h-4 w-4" aria-hidden />{label}</button>;
      })}
    </div>
    <p className="mt-2 text-xs text-muted-foreground">Currently using {resolvedTheme} appearance.</p>
  </div>;
}
